import logging from '@sstdev/lib_logging';
import server from './getFromServer';
import pThrottle from 'p-throttle';
import { dateMax, dateMin } from '../../valueUtilities';
import _SYNC_TYPES from '../../constants/SYNC_TYPES';
import { getKey } from '../../pageLoadStorage';


const SYNC_TYPES = _SYNC_TYPES.__esModule === true ? _SYNC_TYPES.default : _SYNC_TYPES;

//We are rate limited to
//50 requests per path per minute
//300 requests across all paths per minute
//6 requests (paths) max at a single time
//so, we can sync 6 relations in parallel, with every request having to take at least 1.2 seconds
const MAX_REQUEST_PER_TIME_FRAME = 600; //corresponds to USER_RATE_LIMIT
const MAX_PATH_REQUEST_PER_TIME_FRAME = 100; //Corresponds to USER_PATH_RATE_LIMIT
const REQUEST_RATE_TIME_FRAME = 60000; //1 minute in ms
const crossRelationThrottle = pThrottle({
    //throttle at 1 per part of interval, rather than MAX_REQUEST_PER_TIME_FRAME as limit and REQUEST_RATE_TIME_FRAME as interval
    // to avoid an initial surge
    limit: 1,
    interval: REQUEST_RATE_TIME_FRAME / MAX_REQUEST_PER_TIME_FRAME
});

const _p = {
    getThrottledBatch: crossRelationThrottle(server.getBatchWithRetry),
    getThrottledCount: crossRelationThrottle(server.geCountWithRetry),
    getPerRelationThrottle: () =>
        pThrottle({
            //throttle at 1 per part of interval, rather than MAX_REQUEST_PER_TIME_FRAME as limit and REQUEST_RATE_TIME_FRAME as interval
            // to avoid an initial surge
            limit: 1,
            interval: MAX_PATH_REQUEST_PER_TIME_FRAME / MAX_REQUEST_PER_TIME_FRAME
        }),
    removeLocalRecordsOlderThanMinModifiedTime,
    getActiveViewIdsForRelation
};

export const _private = _p;
/**
 * @typedef {{
 *   initialSyncFinished: boolean,
 *   lastSyncRecordCompleted: string,
 *   nextBatchdQueryIndexKey: string,
 *   lastSyncTime: string,
 *   syncStartTime: string,
 *   lastPurgeTime: sting,
 *   lastBatch: boolean
 * }} StorageState
 *
 * @typedef {Array<{namespace:string, relation:string, getStorageState:function():StorageState}>} RelationToSync
 */

/**
 *
 * @param {Object} database
 * @param {function} database.bulkUpsert
 * @param {function} database.relationDb
 * @param {function} database.isNewDatabase
 * @param {function} database.setStorageState
 * @param {function} database.resetStorageState
 * @param {function} database.publish
 * @param {number} maxBatchSize
 */
export default function syncSingleRelation(database, maxBatchSize) {
    /**
     *
     * @param {RelationToSync} relationToSync
     * @param {boolean} obtrusive Whether to keep a progress dialog updated or not (it prevents the user from interacting with the app)
     */
    return async function syncRelation(relationToSync, obtrusive, syncType) {
        //Reject if in progress

        const { namespace, relation } = relationToSync;
        const getStorageState = propertyName => database.getStorageState(namespace.title, relation.title, propertyName);
        // Bail out with the same shape a completed sync resolves with. Callers destructure
        // `result`, and resolving without it handed them `undefined`. This is deliberately NOT a
        // behaviour change: `doingGetMore` tests `results == null || results.length === 0`, so a
        // missing `result` and an empty one take the same branch and both raise the "No additional
        // records were found." toast. What it fixes is the two exit paths disagreeing about what a
        // sync resolves with, which is a trap for the next caller that reads `result` less defensively.
        if (getStorageState().syncStartTime) return { result: [], postPersistence: () => {} };
        const { limitSyncSize = false } = relation;

        logging.debug(`[SYNCHRONIZATION] Syncing ${relation.prettyName || relation.title}`);

        // set defaults value for syncType if not provided.
        if (syncType == null) {
            syncType = limitSyncSize ? SYNC_TYPES.sizeLimitedLatestOnly : SYNC_TYPES.default;
        } else {
            // only allow limitSyncSize relations to perform size limited syncs.
            if (
                !limitSyncSize &&
                [SYNC_TYPES.sizeLimitedFullBatch, SYNC_TYPES.sizeLimitedLatestOnly].includes(syncType)
            ) {
                throw new Error(
                    `${syncType} can only be used with relations having limitSyncSize set to true in Blockly.`
                );
            }
        }

        //Prepare storage
        await prepareStorageState(database, relationToSync);
        //Set in progress
        const syncStartTimeForDuration = new Date();
        const syncStartTime = new Date().toISOString();
        database.setStorageState(namespace.title, relation.title, 'syncStartTime', syncStartTime);

        //get all removed records
        if (getStorageState().lastSyncTime) {
            await processAllBatches(relationToSync, removeFrom(database), {
                header: { ifDeletedSince: getStorageState().lastSyncTime },
                maxBatchSize,
                obtrusive,
                publish: database.publish,
                prefix: 'Cleaning Up ',
                // Deletions page by `_id` like a default sync. Passing no syncType left this
                // capped at a single batch — `maxItemsToSync` fell back to `maxBatchSize`, so the
                // batch loop's `syncedSoFar < maxItemsToSync` was already false once a full batch
                // came back. Anything deleted beyond the first batch survived locally, and
                // permanently: `lastSyncTime` still advances off the insert result below, moving
                // the `ifDeletedSince` window past the records that were never removed.
                // The size-limited sync types deliberately do NOT apply here — they window the
                // record fetch by `meta.serverModifiedTime`, which deleted-record payloads are not
                // guaranteed to carry.
                syncType: SYNC_TYPES.default
            });
        }

        //get all new and updated records
        // `records` is every record fetched across all batches for a size-limited sync (the
        // window bookkeeping below has to see all of them, not just the trailing batch), while
        // `lastBatch` and `totalSynced` carry the two facts that used to be read off the
        // final batch alone.
        const { records, lastBatch, totalSynced } = await processAllBatches(
            relationToSync,
            insertInto(database),
            {
                header: { ifModifiedSince: getStorageState().lastSyncTime },
                earliestModifiedTime: getStorageState().earliestModifiedTime,
                latestModifiedTime: getStorageState().latestModifiedTime,
                maxBatchSize,
                obtrusive,
                publish: database.publish,
                syncType
            }
        );

        // if there are results and this is a size limited sync
        if (
            records.length > 0 &&
            [SYNC_TYPES.sizeLimitedFullBatch, SYNC_TYPES.sizeLimitedLatestOnly].includes(syncType)
        ) {
            // Use results to calculate new latestModifiedTime and earliestModifiedTime.
            // Best-effort: records may lack meta.serverModifiedTime — either all of them (e.g.
            // metadata relations force-limited via limitAllSyncSizes) or only the trailing ones
            // (the batch is sorted by serverModifiedTime descending, so records without it sort to
            // the bottom — see removeLocalRecordsOlderThanMinModifiedTime). Scan from the top for
            // the newest valid timestamp and up from the bottom for the oldest, and only update each
            // window when a valid timestamp is present; otherwise dateMax/dateMin would throw on a
            // cold sync (no stored window) and fail the whole relation sync. Mirrors the datasync path.
            // This scans EVERY record fetched, not just the last batch. Reading the last batch alone
            // meant a trailing empty batch — the normal way a window walk ends — skipped this block
            // entirely, freezing the window so every later Get More re-asked for the same exhausted
            // range and reported "No additional records were found."
            //
            // Note this reads `records` while the truncation cut-off below deliberately reads
            // `lastBatch`. The asymmetry is intentional, because the two failure modes are not
            // symmetric: a wrong cut-off DELETES local data, so it takes the conservative source
            // that yields `undefined` (a no-op) when the final batch has no timestamps, whereas
            // sourcing these bounds from the final batch is exactly what caused #1807 — a trailing
            // empty batch froze the window. The residual cost is that on a mixed batch (timestamped
            // records plus untimestamped ones) this sets bounds where the old code left them null,
            // and the `$or` window cannot match a missing field either way, so records with no
            // `meta.serverModifiedTime` stay unreachable to Get More. That limitation is inherent
            // to windowing on `serverModifiedTime` and predates this code.
            const latestServerModifiedTime = records.find(r => r?.meta?.serverModifiedTime)?.meta
                ?.serverModifiedTime;
            if (latestServerModifiedTime != null || getStorageState().latestModifiedTime != null) {
                const latestModifiedTime = dateMax(
                    latestServerModifiedTime,
                    getStorageState().latestModifiedTime
                );
                database.setStorageState(
                    namespace.title,
                    relation.title,
                    'latestModifiedTime',
                    latestModifiedTime.toISOString()
                );
            }
            let earliestServerModifiedTime;
            for (let i = records.length - 1; i >= 0; i--) {
                if (records[i]?.meta?.serverModifiedTime) {
                    earliestServerModifiedTime = records[i].meta.serverModifiedTime;
                    break;
                }
            }
            if (earliestServerModifiedTime != null || getStorageState().earliestModifiedTime != null) {
                const earliestModifiedTime = dateMin(
                    earliestServerModifiedTime,
                    getStorageState().earliestModifiedTime
                );
                database.setStorageState(
                    namespace.title,
                    relation.title,
                    'earliestModifiedTime',
                    earliestModifiedTime.toISOString()
                );
            }

            // If we get a max sized batch of these, it means that it was almost certainly limited
            // at the server and there are more.  For this reason, it is possible that
            // previously synched records have changed and were not included, so remove local
            // records older than the the oldest synched record to make sure there is no stale
            // data. Incidentally, this will help limit the amount of local data for really
            // high volume tenants. It will not completely cure the problem though, so the
            // truncateCollectionToMaxSize call below is necessary.
            // The FINAL batch (not the accumulated set) is what both halves of this want. Its
            // length carries the meaning: a full last batch is what says the server was still
            // holding records back. And it must also stay the source of the cut-off timestamp --
            // deliberately so. The scan below walks from the end for the oldest
            // `serverModifiedTime`, and when the whole final batch lacks one it yields `undefined`,
            // which `removeOlderRecords` turns into `{$lt: undefined}` -- a criteria LokiJS matches
            // nothing with, so nothing is deleted. Handing it the accumulated records instead would
            // find a real timestamp in an EARLIER batch, and `{$lt: <date>}` DOES match records
            // whose `meta.serverModifiedTime` is missing -- wiping every timestamp-less local row,
            // including ones this very sync just upserted. See #830 for that data shape.
            if (syncType === SYNC_TYPES.sizeLimitedLatestOnly && lastBatch.length === maxBatchSize) {
                await _p.removeLocalRecordsOlderThanMinModifiedTime(
                    relationToSync,
                    lastBatch,
                    removeOlderRecords(database)
                );
            }

            // If the local collection has grown beyond the allowed boundary, remove the oldest
            // records until it is the max size
            const maxSize = database.settings.config.maxRecordsInLimitedSizeCollection;
            const relationTotalSize = database.relationTotalSize({
                namespace: namespace.title,
                relation: relation.title
            });
            if (relationTotalSize > maxSize) {
                const collection = database.relationDb(namespace.title, relation.title);
                const excludedIds = await _p.getActiveViewIdsForRelation(namespace.title, relation.title);
                await collection.truncateCollectionToMaxSize(maxSize, excludedIds);
            }
        }

        const postPersistence = () => {
            // Anything fetched at all advances the watermark — including the case where the run
            // ended on an empty batch after earlier batches delivered records.
            if (totalSynced > 0) {
                //Update last synced dateTime
                database.setStorageState(namespace.title, relation.title, 'lastSyncTime', syncStartTime);
            }
        };
        //Release from in progress
        database.setStorageState(namespace.title, relation.title, 'syncStartTime', undefined);
        database.setStorageState(namespace.title, relation.title, 'initialSyncFinished', true);
        const durationSeconds = (new Date() - syncStartTimeForDuration) / 1000;
        logging.debug(
            `[SYNCHRONIZATION] Syncing ${relation.prettyName || relation.title} Complete. ${durationSeconds} seconds.`
        );

        return { result: records, postPersistence };
    };
}

async function prepareStorageState({ isNewDatabase, resetStorageState }, { namespace, relation }) {
    return isNewDatabase(namespace.title, relation.title).then(isNew => {
        // If the database has been deleted but the storage state
        // was not updated (seems to happen when reinstalling app
        // on android), then reset storage state.
        // Include update sequence in new storage state so this doesn't end up
        // in a loop (see database.isNewDatabase()).
        if (isNew) {
            resetStorageState(namespace.title, relation.title);
        }
    });
}

/**
 * Get (and process) the first batch
 * If the batch is full
 *    if we are providing feedback to the UI:
 *        Do a count for the total number of results
 *    Sync all remaining batches
 * @param {RelationToSync} relationToSync
 * @param {function(RelationToSync, Array<Object>):Promise<void>} operationOnDatabase
 * @param {Object} options
 * @param {Object} options.header
 * @param {string} [options.header.ifDeletedSince]
 * @param {string} [options.header.ifModifiedSince]
 * @param {function} options.publish
 * @param {boolean} options.obtrusive
 * @param {number} options.maxBatchSize
 * @param {string} options.prefix
 * @returns {Promise<{records: Array<Object>, lastBatch: Array<Object>, totalSynced: number}>}
 * `records` is every record fetched for a size-limited sync type, in fetch order (globally
 * descending by `meta.serverModifiedTime`), and only the final batch for `SYNC_TYPES.default` —
 * a default sync can pull 100k+ records and holding them all just to hand them back would be a
 * memory regression, and no default-sync caller looks at them. `lastBatch` is the final batch itself -- its
 * length is the "was the server still limiting us?" signal, and it is also the only safe source
 * for the truncation cut-off timestamp (see the call site) -- and `totalSynced` is the count
 * across every batch.
 */
async function processAllBatches(
    { namespace, relation },
    operationOnDatabase,
    { maxBatchSize, obtrusive, publish, header, prefix = '', earliestModifiedTime, latestModifiedTime, syncType }
) {
    const throttle = _p.getPerRelationThrottle();
    const getThrottledBatch = throttle(_p.getThrottledBatch);
    const getThrottledCount = throttle(_p.getThrottledCount);

    let maxItemsToSync = maxBatchSize;

    if (relation.maxSyncSize && [SYNC_TYPES.sizeLimitedFullBatch, SYNC_TYPES.sizeLimitedLatestOnly].includes(syncType)) {
        maxItemsToSync = relation.maxSyncSize;
    }

    //Get first batch of data (since last synced date)
    let result = await getThrottledBatch(namespace.title, relation.title, {
        ...header,
        lastRecordId: undefined,
        limit: Math.min(maxBatchSize, maxItemsToSync),
        earliestModifiedTime,
        latestModifiedTime,
        syncType
    });
    await operationOnDatabase({ namespace, relation }, result);

    let total = 1;
    let relationSpecificFeedback = false;
    let syncedSoFar = result.length;

    // Only the size-limited sync types accumulate: their caller has to look at every record
    // fetched to move the `meta.serverModifiedTime` window, and their totals are bounded by
    // `maxSyncSize`. A default sync is unbounded, so accumulating it would mean holding an
    // entire relation in memory for a caller that never reads it.
    const accumulateRecords = [SYNC_TYPES.sizeLimitedFullBatch, SYNC_TYPES.sizeLimitedLatestOnly].includes(syncType);
    const allRecords = accumulateRecords ? [...result] : undefined;

    // Whether the batch loop below will actually iterate. Deliberately the same expression the
    // `while` uses, so the two cannot drift apart: a relation progress bar is only worth showing
    // for a relation that is going to fetch more than the batch we already have.
    // The delete pass now passes `SYNC_TYPES.default` too, so it qualifies on the same terms as
    // an ordinary sync — which is what makes its `Cleaning Up ` bar reachable at last.
    const willFetchMoreBatches =
        result.length === maxBatchSize && (syncType === SYNC_TYPES.default || syncedSoFar < maxItemsToSync);

    // If more batches are coming, get statistics for feedback and provide feedback for first batch.
    // This must NOT be restricted to `syncType === default`: size-limited relations are the ones
    // that page the longest, and skipping this left `total` at its 1 initialiser, so the loop below
    // published `current: 1000, total: 1` and the bar sat pinned at 100% for the whole pull.
    if (obtrusive && willFetchMoreBatches && supportsAggregate(namespace, relation)) {
        // show relation progress bar to 0 of 1 to get something going
        relationSpecificFeedback = true;
        publish(
            {
                mainTitle: 'Syncing In Progress...',
                title: `${prefix}${relation.prettyName || relation.title}`,
                current: 0,
                total: 1
            },
            { verb: 'update', namespace: 'application', relation: 'progress' }
        );

        //  Get count of all records for relation from server
        total = await getThrottledCount(namespace.title, relation.title, header);
        total = syncType === SYNC_TYPES.default ? total : Math.min(maxItemsToSync, total);
        // update relation progress bar to maxBatchSize of count
        // as we already synced the first batch
        publish(
            {
                mainTitle: 'Syncing In Progress...',
                title: `${prefix}${relation.prettyName || relation.title}`,
                current: syncedSoFar,
                total: total
            },
            { verb: 'update', namespace: 'application', relation: 'progress' }
        );
    }

    // If last result filled the maximum batch size (so assuming more records are needed),
    // get the rest of the batches of records for the relation. A size-limited relation keeps
    // paging too — up to `maxItemsToSync` — so the parenthetical that used to live here
    // ("unless limitSyncSize is true, in which case only one batch is ever needed") was wrong.
    while ((syncType === SYNC_TYPES.default || syncedSoFar < maxItemsToSync) && result.length === maxBatchSize) {
        const remainingItemsToSync = Math.max(0, maxItemsToSync - syncedSoFar);
        const nextLimit =
            syncType === SYNC_TYPES.default ? maxBatchSize : Math.min(maxBatchSize, remainingItemsToSync);

        if (syncType === SYNC_TYPES.sizeLimitedLatestOnly) {
            // Get batch of data ordered by `meta.serverModifiedTime` descending,
            // older than the last result's `meta.serverModifiedTime`.
            const lastItem = result[result.length - 1];
            result = await getThrottledBatch(namespace.title, relation.title, {
                ...header,
                syncType,
                lastRecordId: lastItem._id,
                lastModifiedTime: lastItem.meta.serverModifiedTime,
                limit: nextLimit
            });
        } else if (syncType === SYNC_TYPES.sizeLimitedFullBatch) {
            // Keep walking the SAME `meta.serverModifiedTime` window the first batch opened.
            // Passing none of this (the old behaviour) let `getBatchQuery` fall back to its
            // `syncType = default` default, so batch 2 silently became a different query
            // altogether — `orderBy: _id ascending` with `_id > lastRecordId`, and
            // `getBatchHeaders` re-attaching the `if-modified-since` this sync type
            // deliberately suppresses. `earliestModifiedTime`/`latestModifiedTime` are safe to
            // resend unchanged: they are not mutated during the loop, only written to storage
            // state once every batch is in.
            //
            // There is no cursor to page by here — every record in the window is either newer
            // than `latestModifiedTime` or older than `earliestModifiedTime`, so neither bound
            // advances as we go. We walk by offset instead. The BOUNDS are fixed for the whole
            // call and `[serverModifiedTime desc, _id desc]` is a total order (`_id` breaks every
            // tie), so on a static result set batch N+1 resumes exactly where batch N stopped.
            //
            // The DATA is not fixed, though. A record written server-side mid-walk gets a new
            // `serverModifiedTime`, which lands it at position 0 of the descending sort — behind
            // a cursor we have already passed — so that one record is missed and one already-read
            // record is re-read (a harmless re-upsert). This is self-correcting and deliberately
            // not defended against: the record was missed precisely BECAUSE it was just modified,
            // so its timestamp is above the `latestModifiedTime` this walk ends up storing, and
            // the next walk picks it up through the `$gt latestModifiedTime` arm.
            result = await getThrottledBatch(namespace.title, relation.title, {
                ...header,
                syncType,
                earliestModifiedTime,
                latestModifiedTime,
                offset: syncedSoFar,
                limit: nextLimit
            });
        } else {
            // Get batch 1 of data (since last synced date & > last result's _id)
            result = await getThrottledBatch(namespace.title, relation.title, {
                ...header,
                lastRecordId: result[result.length - 1]._id,
                limit: nextLimit
            });
        }

        await operationOnDatabase({ namespace, relation }, result);
        // Deliberately not `push(...result)`: the spread passes one argument per record, and
        // `backgroundSyncBatchSize` is env-configurable (BACKGROUND_SYNC_BATCH_SIZE), so a large
        // enough batch would blow V8's argument limit. At the 1000 default it is nowhere near,
        // but a loop costs nothing and removes the ceiling entirely.
        if (accumulateRecords) {
            for (const record of result) allRecords.push(record);
        }
        // update relation progress bar to (prev value + result.length) of count
        syncedSoFar += result.length;
        // Only update a bar we actually put up (and gave a real `total`). Without the
        // `relationSpecificFeedback` check this published a bar with `total: 1` that the
        // teardown below — which does check the flag — would then never remove.
        if (obtrusive && relationSpecificFeedback) {
            // update relation progress bar to maxBatchSize of count
            publish(
                {
                    mainTitle: 'Syncing In Progress...',
                    title: `${prefix}${relation.prettyName || relation.title}`,
                    current: syncedSoFar,
                    total: total
                },
                { verb: 'update', namespace: 'application', relation: 'progress' }
            );
        }
    }

    // Should be finished with all batches for the relation, so provide feedback if necessary.
    if (obtrusive && relationSpecificFeedback) {
        // Remove this relation's progress bar, if necessary.
        // If we never displayed it in the first place, this is a noop.
        publish(
            {
                mainTitle: 'Syncing In Progress...',
                title: `${prefix}${relation.prettyName || relation.title}`
            },
            { verb: 'reset', namespace: 'application', relation: 'progress' }
        );
    }

    return {
        records: accumulateRecords ? allRecords : result,
        lastBatch: result,
        totalSynced: syncedSoFar
    };
}

async function removeLocalRecordsOlderThanMinModifiedTime(relationToSync, result, removeOlderRecords) {
    // Get minimum server modified time for result batch
    // (Batch should be sorted by serverModifiedTime descending,
    // so find first one existing, starting from the bottom and
    // stepping upwards.)
    let minServerModifiedTime;
    for (let i = result.length + 1; i >= 0; i--) {
        minServerModifiedTime = result?.[i]?.meta?.serverModifiedTime;
        if (minServerModifiedTime) break;
    }
    // Ideally, this should be more granular because it is possible
    // that multiple records have the same serverModifiedTime.  However, the overlap
    // is not likely to be significant, so this might be good enough.  If this proves to be
    // a problem somehow, the alternative might involve ordering _ids within serverModifiedTime
    // so the records outside the sync can be emperically identified.
    await removeOlderRecords(relationToSync, minServerModifiedTime);
}

// identity silo does not support aggregate (e.g. count operation) yet
const supportsAggregate = ns => !['identity'].includes(ns.title);

const insertInto =
    database =>
    async ({ namespace, relation }, items) => {
        if (!items || items.length === 0) return;
        return database.bulkUpsert(namespace.title, relation.title, items);
    };

const removeFrom =
    database =>
    async ({ namespace, relation }, items) => {
        if (!items || items.length === 0) return Promise.resolve();
        const db = database.relationDb(namespace.title, relation.title);
        // (really) remove them all from the database.
        return Promise.allSettled(items.map(i => db.remove(i)));
    };

const removeOlderRecords =
    database =>
    async ({ namespace, relation }, oldestAllowedTime) => {
        const db = database.relationDb(namespace.title, relation.title);
        return db.removeMany({ trueDelete: true, criteria: { 'meta.serverModifiedTime': { $lt: oldestAllowedTime } } });
    };

/**
 * Collects `_id`s of records currently visible in any active dbView for the given
 * namespace/relation. Used to protect those records from truncation eviction.
 *
 * @param {string} namespaceTitle
 * @param {string} relationTitle
 * @returns {Promise<Set<string>>}
 */
async function getActiveViewIdsForRelation(namespaceTitle, relationTitle) {
    const dbViewReferences = getKey('dbViewReferences') || {};
    const excludedIds = new Set();

    // Iterate all dbViewReferences and match on `view.namespace`/`view.relation` directly.
    // The dbViewName key is caller-provided and not guaranteed to start with
    // `${namespace}_${relation}_`, so prefix filtering would silently drop valid views.
    for (const viewPromise of Object.values(dbViewReferences)) {
        try {
            const view = await viewPromise;
            if (!view || view.namespace !== namespaceTitle || view.relation !== relationTitle) continue;
            if (Array.isArray(view.data)) {
                for (const record of view.data) {
                    if (record && record._id) excludedIds.add(record._id);
                }
            }
        } catch (err) {
            logging.error(err);
        }
    }
    return excludedIds;
}
